/*
 * @providesModule useMaintenance
 */

import {useEffect, useRef, useState} from 'react';
import {NativeModules, Platform} from 'react-native';
import RESTStore from 'RESTStore';
import Preferences from 'Preferences';
import ClientPropertyConstants, {
  MAINTENANCE_IN_PROGRESS,
  MAINTENANCE_POPUP_HEADING,
  MAINTENANCE_POPUP_SUBHEADING,
  PRE_MAINTENANCE,
} from 'ClientPropertyConstants';
import {TelemetryEventName} from 'TelemetryConstants';
import {trackTelemetryEvent} from 'TrackTelemetryEvent';
import GlobalEventEmitter from 'GlobalEventEmitter';

const SKZAPIManager = NativeModules.SKZAPIManager;
const PRE_MAINTENANCE_POLLING_INTERVAL = 10000;
const POST_POPUP_POLLING_INTERVAL = 10 * 60 * 1000;
const SETTINGS_KEY = Platform.OS === 'ios' ? 'clientPropertySettings' : 'settings';
// Web stores flat key-values in theme.settings; watch these keys for maintenance updates
const MAINTENANCE_WATCH_KEYS = [
  MAINTENANCE_IN_PROGRESS,
  PRE_MAINTENANCE,
  MAINTENANCE_POPUP_HEADING,
  MAINTENANCE_POPUP_SUBHEADING,
];

interface SettingCell {
  key: string;
  value: string;
}

export const useMaintenance = () => {
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const preferencesWatchIdRef = useRef<number | null>(null);
  const maintenanceActiveRef = useRef<boolean>(false);
  const [isMaintenanceActive, setIsMaintenanceActive] = useState(false);

  useEffect(() => {
    const stopPolling = () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };

    const updateSettingsFromResponse = (settingsArray: SettingCell[]) => {
      if (!settingsArray || !Array.isArray(settingsArray)) return;
      if (Platform.OS === 'web') {
        const settingsObj: Record<string, string> = {};
        settingsArray.forEach((cell: SettingCell) => {
          if (cell.key) settingsObj[cell.key] = cell.value;
        });
        Preferences.set(settingsObj);
        return;
      }
      const existingSettings = Preferences.get(SETTINGS_KEY);
      let settingsObj: Record<string, string> = {};
      if (existingSettings) {
        try {
          settingsObj = JSON.parse(existingSettings);
        } catch {
          settingsObj = {};
        }
      }
      settingsArray.forEach((cell: SettingCell) => {
        if (cell.key) {
          settingsObj[cell.key] = cell.value;
        }
      });
      Preferences.set({[SETTINGS_KEY]: JSON.stringify(settingsObj)});
    };

    const showMaintenancePopup = () => {
      maintenanceActiveRef.current = true;
      setIsMaintenanceActive(true);
      GlobalEventEmitter.emit('MaintenanceStatusChanged' as any, true);
      trackTelemetryEvent({
        event: TelemetryEventName.MAINTENANCE_POPUP_SHOWN,
        payload: {
          platform: Platform.OS,
        }
      });
    };

    const hideMaintenancePopup = () => {
      maintenanceActiveRef.current = false;
      setIsMaintenanceActive(false);
      GlobalEventEmitter.emit('MaintenanceStatusChanged' as any, false);
      trackTelemetryEvent({
        event: TelemetryEventName.MAINTENANCE_POPUP_HIDE,
        payload: {
          platform: Platform.OS,
        }
      });
    };

    const startPostPopupPolling = () => {
      stopPolling();
      pollingIntervalRef.current = setInterval(() => {
        fetchSettingsAndCheck();
      }, POST_POPUP_POLLING_INTERVAL);
    };

    const startPreMaintenancePolling = () => {
      stopPolling();
      pollingIntervalRef.current = setInterval(() => {
        fetchSettingsAndCheck();
      }, PRE_MAINTENANCE_POLLING_INTERVAL);
    };

    const fetchSettingsAndCheck = () => {
      RESTStore.getServerResponse(
        'settings',
        null,
        SKZAPIManager.OnError.failSilently,
        (response: SettingCell[], error: any) => {
          if (error) {
            if (maintenanceActiveRef.current) {
              hideMaintenancePopup();
              startPreMaintenancePolling();
            }
            return;
          }
          if (response) {
            updateSettingsFromResponse(response);
          }
          checkMaintenanceStatus();
        }
      );
    };

    const checkMaintenanceStatus = () => {
      const inProgress = ClientPropertyConstants.getIsMaintenanceInProgress();
      const preMaintenance = ClientPropertyConstants.getIsPreMaintenance();

      if (inProgress) {
        if (!maintenanceActiveRef.current) {
          showMaintenancePopup();
          startPostPopupPolling();
        }
      } else {
        if (maintenanceActiveRef.current) {
          hideMaintenancePopup();
          if (preMaintenance) {
            startPreMaintenancePolling();
          } else {
            stopPolling();
          }
        } else if (preMaintenance) {
          if (!pollingIntervalRef.current) {
            startPreMaintenancePolling();
          }
        } else {
          stopPolling();
        }
      }
    };

    preferencesWatchIdRef.current =
      Platform.OS === 'web'
        ? Preferences.watchKeys(MAINTENANCE_WATCH_KEYS, () => checkMaintenanceStatus())
        : Preferences.watchKeys(SETTINGS_KEY, () => checkMaintenanceStatus());

    checkMaintenanceStatus();

    if (Platform.OS === 'web') {
      fetchSettingsAndCheck();
    }

    return () => {
      stopPolling();
      if (preferencesWatchIdRef.current !== null) {
        Preferences.clearWatch(preferencesWatchIdRef.current);
        preferencesWatchIdRef.current = null;
      }
    };
  }, []);

  return {
    isMaintenanceActive,
    heading: isMaintenanceActive
      ? ClientPropertyConstants.getMaintenancePopupHeading()
      : '',
    subheading: isMaintenanceActive
      ? ClientPropertyConstants.getMaintenancePopupSubheading()
      : '',
  };
};
